import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Megaphone, Storefront, ArrowsLeftRight, RocketLaunch, CheckCircle } from "@phosphor-icons/react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp, Overline } from "../components/Reveal";

const MODELS = [
  {
    icon: Megaphone,
    title: "Refer & Earn",
    text: "Introduce us to a hospital, lab, pharma or payer. We close and deliver; you earn a referral fee on the deal — no delivery burden, no overhead.",
  },
  {
    icon: Storefront,
    title: "Resell",
    text: "Take Eaglei, PatExcite, Clinixify, MasterCXM or GENEiuX to your customers under your commercial relationship, with partner margins built in.",
  },
  {
    icon: ArrowsLeftRight,
    title: "Cross-sell",
    text: "Your offering plus ours, bundled for shared customers. Devices meet data platforms; services meet products — deals get bigger for both of us.",
  },
  {
    icon: RocketLaunch,
    title: "Co-build & Upsell",
    text: "Bring us a client challenge and we co-build the solution — then expand the account together. Every engagement compounds into the next.",
  },
];

const BENEFITS = [
  "AI-native economics — our costs are low, so partner margins are genuinely fat",
  "30-day delivery culture — partners look fast because we are",
  "Deal registration and protection on every named account",
  "Free enablement: demos, decks, and solution engineering support",
  "Co-marketing: joint case studies, webinars, and event presence",
  "Compliance-ready delivery — 21 CFR Part 11, ISO 17025, GxP fluency",
  "Dedicated partner channel into our team — real humans, fast answers",
  "Revenue share that grows the more we win together",
];

export default function Partners() {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  const partnerLink = ref ? `/demo?ref=${encodeURIComponent(ref)}&via=partners` : "/demo?via=partners";
  return (
    <>
      <PageHeader
        testId="partners-page"
        overline="Partners"
        title={["Partner for the", "AI-native advantage."]}
        subtitle="Sell, resell, cross-sell, upsell — AI-native cost, quality, compliance and time-to-market for your clients, better patient experience, and real margin for you."
      />

      <section className="pb-24 lg:pb-32" data-testid="partner-models">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp><Overline>Four ways to partner</Overline></FadeUp>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {MODELS.map((m, i) => (
              <FadeUp key={m.title} delay={i * 0.06}>
                <div className="flex h-full flex-col rounded-2xl border border-primary/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50 hover:shadow-xl hover:shadow-accent/10" data-testid={`partner-model-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/5 text-primary">
                    <m.icon size={24} weight="duotone" />
                  </span>
                  <h2 className="mt-6 font-display text-2xl font-medium tracking-tight text-foreground">{m.title}</h2>
                  <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">{m.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-primary text-white" data-testid="partner-benefits">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>Partner benefits</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-2xl">
              Lean for us means lucrative for you
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {BENEFITS.map((b, i) => (
              <FadeUp key={b} delay={i * 0.04}>
                <div className="flex items-start gap-3 rounded-xl border border-white/15 bg-white/5 px-5 py-4" data-testid={`partner-benefit-${i}`}>
                  <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-accent" />
                  <p className="text-sm sm:text-base font-medium text-white/90 leading-relaxed">{b}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.25} className="mt-12">
            <p className="max-w-3xl text-sm text-white/50 leading-relaxed">
              We don't run on aggressive margins or investor pressure — so partnership economics stay generous. You bring the relationship; we bring AI-native speed. Everyone's customer wins.
            </p>
            <Link
              to={partnerLink}
              data-testid="partners-talk-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
            >
              Become a Partner <ArrowUpRight size={16} weight="bold" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
